import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  Card,
  Space,
  Avatar,
  Tag,
  Image,
  Modal,
  message,
  Row,
  Col,
  InputNumber,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  PlusOutlined,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import StaffService from "../services/staff.service";
import ImageUpload from "../components/ImageUpload";
import {
  StaffMember,
  StaffSearchParams,
  GenderType,
  StaffState,
  CustomerResponse,
  CustomerSearchParams,
  convertStaffResponseToMember,
  convertFormToApiData,
  convertCreateFormToApiData,
  ImageUploadValues,
  StaffStatsFormData,
  StaffProfileFormData,
} from "../types/staff.types";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

// 默认列宽配置
const DEFAULT_COLUMN_WIDTHS = {
  id: 100,
  avatar: 80,
  name: 100,
  gender: 60,
  age: 60,
  phone: 130,
  address: 200,
  idCard: 180,
  idCardImage: 100,
  healthCertImage: 100,
  joinDate: 100,
  serviceCount: 100,
  customerCount: 100,
  rating: 100,
  status: 90,
  action: 120,
};

interface SearchParams {
  name?: string;
  phone?: string;
  idCard?: string;
  gender?: "男" | "女";
  address?: string;
  customerName?: string;
  status?: "待审核" | "在职" | "已离职";
  joinDateRange?: [Dayjs, Dayjs];
}

interface ApproveFormValues {
  name: string;
  gender: "男" | "女";
  age: number;
  address: string;
  phone: string;
  idCard: string;
  joinDate: Dayjs;
  title?: string; // 职位标签
}

interface LeaveFormValues {
  leaveDate: Dayjs;
  leaveReason: string;
}

interface CustomerSearchFormValues {
  phone: string;
}

interface StatsFormValues {
  serviceCount: number;
  customerCount: number;
  rating: string;
}

// 与ApproveFormValues相同的接口，用于修改资料
interface ProfileFormValues {
  name: string;
  gender: "男" | "女";
  age: number;
  address: string;
  phone: string;
  idCard: string;
  joinDate: Dayjs;
  title?: string; // 职位标签
}

const StaffManagement: React.FC = () => {
  const [form] = Form.useForm<SearchParams>();
  const [approveForm] = Form.useForm<ApproveFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<StaffMember[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [approveModalVisible, setApproveModalVisible] =
    useState<boolean>(false);
  const [currentRecord, setCurrentRecord] = useState<StaffMember | null>(null);
  const [leaveModalVisible, setLeaveModalVisible] = useState<boolean>(false);
  const [leaveForm] = Form.useForm<LeaveFormValues>();

  // 修改统计相关状态
  const [statsModalVisible, setStatsModalVisible] = useState<boolean>(false);
  const [statsForm] = Form.useForm<StatsFormValues>();

  // 修改资料相关状态
  const [profileModalVisible, setProfileModalVisible] =
    useState<boolean>(false);
  const [profileForm] = Form.useForm<ProfileFormValues>();

  // 新增员工相关状态
  const [addStaffModalVisible, setAddStaffModalVisible] =
    useState<boolean>(false);
  const [customerSearchModalVisible, setCustomerSearchModalVisible] =
    useState<boolean>(false);
  const [customerSearchForm] = Form.useForm<CustomerSearchFormValues>();
  const [customerDataSource, setCustomerDataSource] = useState<
    CustomerResponse[]
  >([]);
  const [customerLoading, setCustomerLoading] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerResponse | null>(null);

  // 图片上传值管理（替换原有的文件列表状态）
  const [imageUploadValues, setImageUploadValues] = useState<ImageUploadValues>(
    {
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    }
  );

  // 图片上传值更新函数
  const updateImageUploadValue = (
    key: keyof ImageUploadValues,
    value: string | undefined
  ) => {
    setImageUploadValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 重置列宽度
  const handleResetColumnWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    message.success("列宽度已重置");
  };

  // 初始化数据
  useEffect(() => {
    handleSearch();
  }, []);

  // 搜索功能
  const handleSearch = async (values: SearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    try {
      // 构建API请求参数
      const apiParams: StaffSearchParams = {
        page: pagination.current || 1,
        limit: pagination.pageSize || 20,
        name: values.name,
        phone: values.phone,
        idCard: values.idCard,
        gender:
          values.gender === "男"
            ? GenderType.MALE
            : values.gender === "女"
            ? GenderType.FEMALE
            : undefined,
        address: values.address,
        customerName: values.customerName,
        state: values.status
          ? values.status === "待审核"
            ? StaffState.PENDING
            : values.status === "在职"
            ? StaffState.ACTIVE
            : StaffState.RESIGNED
          : undefined,
      };

      // 处理日期范围
      if (values.joinDateRange && values.joinDateRange.length === 2) {
        apiParams.joinDateStart = values.joinDateRange[0].format("YYYY-MM-DD");
        apiParams.joinDateEnd = values.joinDateRange[1].format("YYYY-MM-DD");
      }

      // 调用API
      const response = await StaffService.getServiceStaffList(apiParams);

      if (response.success) {
        // 转换数据格式
        const staffMembers = response.data.items.map(
          convertStaffResponseToMember
        );
        setDataSource(staffMembers);

        // 更新分页信息
        setPagination((prev) => ({
          ...prev,
          total: response.data.meta.total,
          current: response.data.meta.page,
          pageSize: response.data.meta.limit,
        }));
      } else {
        message.error("获取员工列表失败");
        setDataSource([]);
      }
    } catch (error) {
      console.error("搜索员工失败:", error);
      message.error("搜索员工失败，请重试");
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);

    // 使用新的分页参数重新搜索
    const newSearchParams = {
      ...searchParams,
      page: newPagination.current || 1,
      limit: newPagination.pageSize || 20,
    };

    handleSearch(newSearchParams);
  };

  // 审核入职 - 打开弹窗
  const handleApprove = (record: StaffMember) => {
    setCurrentRecord(record);
    setApproveModalVisible(true);

    // 清空图片上传值（先清空，避免状态污染）
    setImageUploadValues({
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    });

    // 初始化表单数据
    approveForm.setFieldsValue({
      name: record.name,
      gender: record.gender,
      age: record.age,
      address: record.address,
      phone: record.phone,
      idCard: record.idCard,
      joinDate: dayjs(), // 默认为当前日期
    });
  };

  // 确认审核入职
  const handleApproveConfirm = async () => {
    try {
      const values = await approveForm.validateFields();

      if (!currentRecord) {
        message.error("未选择员工记录");
        return;
      }

      // 转换表单数据
      const formData = convertFormToApiData(values);

      // 准备文件数据 - 现在使用图片URL而不是文件对象
      const files: ImageUploadValues = {
        avatar: imageUploadValues.avatar,
        idCardFront: imageUploadValues.idCardFront,
        idCardBack: imageUploadValues.idCardBack,
        healthCert: imageUploadValues.healthCert,
      };

      setLoading(true);
      const response = await StaffService.approveStaff(
        currentRecord.id,
        formData,
        files
      );

      if (response.success) {
        message.success("审核成功");
        setApproveModalVisible(false);

        // 重新加载数据
        handleSearch(searchParams);

        // 先清空图片上传值
        setImageUploadValues({
          avatar: undefined,
          idCardFront: undefined,
          idCardBack: undefined,
          healthCert: undefined,
        });

        // 然后清空表单
        approveForm.resetFields();
      } else {
        message.error("审核失败，请重试");
      }
    } catch (error) {
      console.error("审核入职失败:", error);
      message.error("审核失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 取消审核
  const handleApproveCancel = () => {
    setApproveModalVisible(false);
    setCurrentRecord(null);

    // 先清空图片上传值
    setImageUploadValues({
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    });

    // 然后重置表单
    approveForm.resetFields();
  };

  // 办理离职
  const handleLeave = (record: StaffMember) => {
    setCurrentRecord(record);
    setLeaveModalVisible(true);
    // 初始化表单数据
    leaveForm.setFieldsValue({
      leaveDate: dayjs(),
      leaveReason: "",
    });
  };

  // 确认办理离职
  const handleLeaveConfirm = async () => {
    try {
      const values = await leaveForm.validateFields();

      if (!currentRecord) {
        message.error("未选择员工记录");
        return;
      }

      // 转换表单数据
      const formData = {
        leaveDate: values.leaveDate.format("YYYY-MM-DD"),
        leaveReason: values.leaveReason,
      };

      setLoading(true);
      const response = await StaffService.leaveStaff(
        currentRecord.id,
        formData
      );

      if (response.success) {
        message.success("离职办理成功");
        setLeaveModalVisible(false);

        // 重新加载数据
        handleSearch(searchParams);

        // 清空表单
        leaveForm.resetFields();
      } else {
        message.error("离职办理失败，请重试");
      }
    } catch (error) {
      console.error("办理离职失败:", error);
      message.error("办理离职失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 取消办理离职
  const handleLeaveCancel = () => {
    setLeaveModalVisible(false);
    setCurrentRecord(null);
    leaveForm.resetFields();
  };

  // 新增员工 - 打开客户搜索弹窗
  const handleAddStaff = () => {
    setCustomerSearchModalVisible(true);
    customerSearchForm.resetFields();
    setCustomerDataSource([]);
    setSelectedCustomer(null);
  };

  // 搜索客户
  const handleSearchCustomers = async (values: CustomerSearchFormValues) => {
    setCustomerLoading(true);
    try {
      const params: CustomerSearchParams = {
        phone: values.phone,
        page: 1,
        limit: 10,
      };

      const response = await StaffService.searchCustomersForStaff(params);

      if (response.success) {
        setCustomerDataSource(response.data.items);
      } else {
        message.error("搜索客户失败");
        setCustomerDataSource([]);
      }
    } catch (error) {
      console.error("搜索客户失败:", error);
      message.error("搜索客户失败，请重试");
      setCustomerDataSource([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  // 选中客户为员工
  const handleSelectCustomerAsStaff = (customer: CustomerResponse) => {
    setSelectedCustomer(customer);
    setCustomerSearchModalVisible(false);
    setAddStaffModalVisible(true);

    // 清空图片上传值（先清空，避免状态污染）
    setImageUploadValues({
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    });

    // 初始化表单数据（使用客户信息）
    approveForm.setFieldsValue({
      name: customer.name || "",
      gender: customer.genderDisplay as "男" | "女",
      phone: customer.phone || "",
      joinDate: dayjs(), // 默认为当前日期
      age: undefined, // 清空年龄
      address: undefined, // 清空地址
      idCard: undefined, // 清空身份证号
    });
  };

  // 确认新增员工
  const handleAddStaffConfirm = async () => {
    try {
      const values = await approveForm.validateFields();

      if (!selectedCustomer) {
        message.error("未选择客户");
        return;
      }

      // 转换表单数据
      const formData = convertCreateFormToApiData(
        values,
        selectedCustomer.commonUserId
      );

      // 准备文件数据 - 现在使用图片URL而不是文件对象
      const files: ImageUploadValues = {
        avatar: imageUploadValues.avatar,
        idCardFront: imageUploadValues.idCardFront,
        idCardBack: imageUploadValues.idCardBack,
        healthCert: imageUploadValues.healthCert,
      };

      setLoading(true);
      const response = await StaffService.createStaff(formData, files);

      if (response.success) {
        message.success("新增员工成功");
        setAddStaffModalVisible(false);

        // 重新加载数据
        handleSearch(searchParams);

        // 先清空图片上传值
        setImageUploadValues({
          avatar: undefined,
          idCardFront: undefined,
          idCardBack: undefined,
          healthCert: undefined,
        });

        // 然后清空表单和选中客户
        approveForm.resetFields();
        setSelectedCustomer(null);
      } else {
        message.error("新增员工失败，请重试");
      }
    } catch (error) {
      console.error("新增员工失败:", error);
      message.error("新增员工失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 取消新增员工
  const handleAddStaffCancel = () => {
    setAddStaffModalVisible(false);
    setSelectedCustomer(null);

    // 先清空图片上传值
    setImageUploadValues({
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    });

    // 然后重置表单
    approveForm.resetFields();
  };

  // 取消客户搜索
  const handleCustomerSearchCancel = () => {
    setCustomerSearchModalVisible(false);
    customerSearchForm.resetFields();
    setCustomerDataSource([]);
    setSelectedCustomer(null);
  };

  // 修改统计 - 打开弹窗
  const handleEditStats = (record: StaffMember) => {
    setCurrentRecord(record);
    setStatsModalVisible(true);

    // 初始化表单数据
    statsForm.setFieldsValue({
      serviceCount: record.serviceCount,
      customerCount: record.customerCount,
      rating: record.rating,
    });
  };

  // 确认修改统计
  const handleStatsConfirm = async () => {
    try {
      const values = await statsForm.validateFields();

      if (!currentRecord) {
        message.error("未选择员工记录");
        return;
      }

      // 转换表单数据
      const statsData: StaffStatsFormData = {
        serviceCount: values.serviceCount,
        customerCount: values.customerCount,
        rating: values.rating,
      };

      setLoading(true);
      const response = await StaffService.updateStaffIndexes(
        currentRecord.id,
        statsData
      );

      if (response.success) {
        message.success("统计信息修改成功");
        setStatsModalVisible(false);

        // 重新加载数据
        handleSearch(searchParams);

        // 清空表单
        statsForm.resetFields();
      } else {
        message.error("统计信息修改失败，请重试");
      }
    } catch (error) {
      console.error("修改统计信息失败:", error);
      message.error("修改统计信息失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 取消修改统计
  const handleStatsCancel = () => {
    setStatsModalVisible(false);
    setCurrentRecord(null);
    statsForm.resetFields();
  };

  // 修改资料 - 打开弹窗
  const handleEditProfile = (record: StaffMember) => {
    setCurrentRecord(record);
    setProfileModalVisible(true);

    // 初始化图片上传值
    // 从身份证图片数组中获取正面和反面图片
    const idCardImages = record.idCardImages || [];
    setImageUploadValues({
      avatar: record.avatar || undefined,
      idCardFront: idCardImages[0] || undefined, // 第一张是正面
      idCardBack: idCardImages[1] || undefined, // 第二张是反面
      healthCert: record.healthCertImage || undefined,
    });

    // 初始化表单数据
    profileForm.setFieldsValue({
      name: record.name,
      gender: record.gender,
      age: record.age,
      address: record.address,
      phone: record.phone,
      idCard: record.idCard,
      joinDate: dayjs(record.joinDate),
      title: record.title || "",
    });
  };

  // 确认修改资料
  const handleProfileConfirm = async () => {
    try {
      const values = await profileForm.validateFields();

      if (!currentRecord) {
        message.error("未选择员工记录");
        return;
      }

      // 转换表单数据
      const profileData: StaffProfileFormData = {
        name: values.name,
        gender: values.gender === "男" ? GenderType.MALE : GenderType.FEMALE,
        age: values.age,
        address: values.address,
        phone: values.phone,
        idCard: values.idCard,
        joinDate: values.joinDate.format("YYYY-MM-DD"),
        title: values.title,
      };

      // 准备图片数据
      const files: ImageUploadValues = {
        avatar: imageUploadValues.avatar,
        idCardFront: imageUploadValues.idCardFront,
        idCardBack: imageUploadValues.idCardBack,
        healthCert: imageUploadValues.healthCert,
      };

      setLoading(true);
      const response = await StaffService.updateStaffProfile(
        currentRecord.id,
        profileData,
        files
      );

      if (response.success) {
        message.success("员工资料修改成功");
        setProfileModalVisible(false);

        // 重新加载数据
        handleSearch(searchParams);

        // 清空图片上传值
        setImageUploadValues({
          avatar: undefined,
          idCardFront: undefined,
          idCardBack: undefined,
          healthCert: undefined,
        });

        // 清空表单
        profileForm.resetFields();
      } else {
        message.error("员工资料修改失败，请重试");
      }
    } catch (error) {
      console.error("修改员工资料失败:", error);
      message.error("修改员工资料失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  // 取消修改资料
  const handleProfileCancel = () => {
    setProfileModalVisible(false);
    setCurrentRecord(null);

    // 清空图片上传值
    setImageUploadValues({
      avatar: undefined,
      idCardFront: undefined,
      idCardBack: undefined,
      healthCert: undefined,
    });

    profileForm.resetFields();
  };

  const columns: ColumnsType<StaffMember> = [
    {
      title: "编号",
      dataIndex: "id",
      key: "id",
      width: columnWidths.id,
      onHeaderCell: () => ({
        width: columnWidths.id,
        onResize: handleResize("id"),
      }),
    },
    {
      title: "头像",
      dataIndex: "avatar",
      key: "avatar",
      width: columnWidths.avatar,
      onHeaderCell: () => ({
        width: columnWidths.avatar,
        onResize: handleResize("avatar"),
      }),
      render: (avatar: string, record: StaffMember) => (
        <Avatar src={avatar} size={40}>
          {record.name.charAt(0)}
        </Avatar>
      ),
    },
    {
      title: "姓名",
      dataIndex: "name",
      key: "name",
      width: columnWidths.name,
      onHeaderCell: () => ({
        width: columnWidths.name,
        onResize: handleResize("name"),
      }),
    },
    {
      title: "性别",
      dataIndex: "gender",
      key: "gender",
      width: columnWidths.gender,
      onHeaderCell: () => ({
        width: columnWidths.gender,
        onResize: handleResize("gender"),
      }),
    },
    {
      title: "年龄",
      dataIndex: "age",
      key: "age",
      width: columnWidths.age,
      onHeaderCell: () => ({
        width: columnWidths.age,
        onResize: handleResize("age"),
      }),
    },
    {
      title: "手机号",
      dataIndex: "phone",
      key: "phone",
      width: columnWidths.phone,
      onHeaderCell: () => ({
        width: columnWidths.phone,
        onResize: handleResize("phone"),
      }),
    },
    {
      title: "家庭住址",
      dataIndex: "address",
      key: "address",
      width: columnWidths.address,
      ellipsis: true,
      onHeaderCell: () => ({
        width: columnWidths.address,
        onResize: handleResize("address"),
      }),
    },
    {
      title: "身份证号",
      dataIndex: "idCard",
      key: "idCard",
      width: columnWidths.idCard,
      onHeaderCell: () => ({
        width: columnWidths.idCard,
        onResize: handleResize("idCard"),
      }),
    },
    {
      title: "身份证",
      dataIndex: "idCardImage",
      key: "idCardImage",
      width: columnWidths.idCardImage,
      onHeaderCell: () => ({
        width: columnWidths.idCardImage,
        onResize: handleResize("idCardImage"),
      }),
      render: (image: string, record: StaffMember) => {
        // 优先使用 idCardImage，如果没有则使用 idCardImages 的第一张
        const displayImage = image || record.idCardImages?.[0] || "";
        return (
          <Image
            width={40}
            height={30}
            src={displayImage}
            style={{ objectFit: "cover" }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
          />
        );
      },
    },
    {
      title: "健康证",
      dataIndex: "healthCertImage",
      key: "healthCertImage",
      width: columnWidths.healthCertImage,
      onHeaderCell: () => ({
        width: columnWidths.healthCertImage,
        onResize: handleResize("healthCertImage"),
      }),
      render: (image: string) => (
        <Image
          width={40}
          height={30}
          src={image}
          style={{ objectFit: "cover" }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
        />
      ),
    },
    {
      title: "入职日期",
      dataIndex: "joinDate",
      key: "joinDate",
      width: columnWidths.joinDate,
      onHeaderCell: () => ({
        width: columnWidths.joinDate,
        onResize: handleResize("joinDate"),
      }),
    },
    {
      title: "服务次数",
      dataIndex: "serviceCount",
      key: "serviceCount",
      width: columnWidths.serviceCount,
      onHeaderCell: () => ({
        width: columnWidths.serviceCount,
        onResize: handleResize("serviceCount"),
      }),
    },
    {
      title: "客户数",
      dataIndex: "customerCount",
      key: "customerCount",
      width: columnWidths.customerCount,
      onHeaderCell: () => ({
        width: columnWidths.customerCount,
        onResize: handleResize("customerCount"),
      }),
    },
    {
      title: "服务评价",
      dataIndex: "rating",
      key: "rating",
      width: columnWidths.rating,
      onHeaderCell: () => ({
        width: columnWidths.rating,
        onResize: handleResize("rating"),
      }),
      render: (rating: string) => {
        const ratingNum = parseFloat(rating);
        let color = "inherit";

        if (ratingNum > 4.0) {
          color = "#52c41a"; // 绿色
        } else if (ratingNum >= 3.0) {
          color = "#faad14"; // 黄色
        } else {
          color = "#f5222d"; // 红色
        }

        return <span style={{ color, fontWeight: "bold" }}>{rating}/5.0</span>;
      },
    },
    {
      title: "在职状态",
      dataIndex: "status",
      key: "status",
      width: columnWidths.status,
      onHeaderCell: () => ({
        width: columnWidths.status,
        onResize: handleResize("status"),
      }),
      render: (status: StaffMember["status"]) => {
        const colorMap = {
          待审核: "orange",
          在职: "green",
          已离职: "red",
        };
        return <Tag color={colorMap[status]}>{status}</Tag>;
      },
    },
    {
      title: "操作",
      key: "action",
      width: columnWidths.action,
      fixed: "right",
      onHeaderCell: () => ({
        width: columnWidths.action,
        onResize: handleResize("action"),
      }),
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.status === "待审核" && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleApprove(record)}
            >
              审核入职
            </Button>
          )}
          {record.status === "在职" && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditStats(record)}
              >
                修改统计
              </Button>
              <Button
                type="default"
                size="small"
                icon={<UserOutlined />}
                onClick={() => handleEditProfile(record)}
              >
                修改资料
              </Button>
              {/* <Button
                type="primary"
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={() => handleLeave(record)}
              >
                办理离职
              </Button> */}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={20} style={{ width: "100%" }}>
            <Col span={6}>
              <Form.Item name="name" label="姓名">
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="phone" label="手机号">
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="idCard" label="身份证号">
                <Input placeholder="请输入身份证号" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20} style={{ width: "100%", marginTop: 16 }}>
            <Col span={6}>
              <Form.Item name="joinDateRange" label="入职日期">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="address" label="家庭住址">
                <Input placeholder="请输入住址" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="status" label="在职状态">
                <Select placeholder="请选择状态" allowClear>
                  <Option value="待审核">待审核</Option>
                  <Option value="在职">在职</Option>
                  <Option value="已离职">已离职</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="gender" label="性别">
                <Select placeholder="请选择性别" allowClear>
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddStaff}
              >
                新增员工
              </Button>
              <Button
                icon={<SearchOutlined />}
                htmlType="submit"
                loading={loading}
              >
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置搜索
              </Button>
              {/* <Button onClick={handleResetColumnWidths}>重置列宽</Button> */}
            </Space>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1800 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 审核入职弹窗 */}
      <Modal
        title="审核入职"
        open={approveModalVisible}
        onOk={handleApproveConfirm}
        onCancel={handleApproveCancel}
        width={800}
        okText="确认审核"
        cancelText="取消"
      >
        <Form
          form={approveForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: "请输入姓名" }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: "请选择性别" }]}
              >
                <Select placeholder="请选择性别">
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="age"
                label="年龄"
                rules={[{ required: true, message: "请输入年龄" }]}
              >
                <InputNumber
                  placeholder="请输入年龄"
                  min={18}
                  max={65}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="联系手机"
                rules={[
                  { required: true, message: "请输入手机号" },
                  { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="家庭住址"
            rules={[{ required: true, message: "请输入家庭住址" }]}
          >
            <Input.TextArea placeholder="请输入家庭住址" rows={2} />
          </Form.Item>

          <Form.Item
            name="idCard"
            label="身份证号码"
            rules={[
              { required: true, message: "请输入身份证号码" },
              {
                pattern:
                  /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dX]$/,
                message: "请输入正确的身份证号码",
              },
            ]}
          >
            <Input placeholder="请输入身份证号码" />
          </Form.Item>

          <Form.Item
            name="joinDate"
            label="入职日期"
            rules={[{ required: true, message: "请选择入职日期" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="title" label="职位标签">
            <Input placeholder="请输入职位标签（如：高级护理师、护理助理等）" />
          </Form.Item>

          <Form.Item label="头像">
            <ImageUpload
              value={imageUploadValues.avatar}
              onChange={(url) => updateImageUploadValue("avatar", url)}
              uploadPath="avatars/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="身份证正面照片"
                rules={[{ required: true, message: "请上传身份证正面照片" }]}
              >
                <ImageUpload
                  value={imageUploadValues.idCardFront}
                  onChange={(url) => updateImageUploadValue("idCardFront", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="身份证反面照片"
                rules={[{ required: true, message: "请上传身份证反面照片" }]}
              >
                <ImageUpload
                  value={imageUploadValues.idCardBack}
                  onChange={(url) => updateImageUploadValue("idCardBack", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="健康证照片"
            rules={[{ required: true, message: "请上传健康证照片" }]}
          >
            <ImageUpload
              value={imageUploadValues.healthCert}
              onChange={(url) => updateImageUploadValue("healthCert", url)}
              uploadPath="health-certs/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 办理离职弹窗 */}
      <Modal
        title="办理离职"
        open={leaveModalVisible}
        onOk={handleLeaveConfirm}
        onCancel={handleLeaveCancel}
        width={600}
        okText="确认离职"
        cancelText="取消"
      >
        <Form form={leaveForm} layout="vertical">
          <Form.Item
            name="leaveDate"
            label="离职日期"
            rules={[{ required: true, message: "请选择离职日期" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="leaveReason"
            label="离职原因"
            rules={[{ required: true, message: "请输入离职原因" }]}
          >
            <Input.TextArea placeholder="请输入离职原因" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 客户搜索弹窗 */}
      <Modal
        title="搜索新注册员工"
        open={customerSearchModalVisible}
        onCancel={handleCustomerSearchCancel}
        width={800}
        footer={null}
      >
        <Form
          form={customerSearchForm}
          layout="inline"
          onFinish={handleSearchCustomers}
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            name="phone"
            label="手机号"
            rules={[{ required: true, message: "请输入手机号" }]}
          >
            <Input placeholder="请输入手机号（支持部分匹配）" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={customerLoading}
              icon={<SearchOutlined />}
            >
              搜索
            </Button>
          </Form.Item>
        </Form>

        <Table
          columns={[
            {
              title: "头像",
              dataIndex: "avatar",
              key: "avatar",
              width: 60,
              render: (avatar: string, record: CustomerResponse) => (
                <Avatar src={avatar} size={40}>
                  {record.name?.charAt(0)}
                </Avatar>
              ),
            },
            {
              title: "姓名",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "性别",
              dataIndex: "genderDisplay",
              key: "genderDisplay",
            },
            {
              title: "手机号",
              dataIndex: "phone",
              key: "phone",
            },
            {
              title: "创建时间",
              dataIndex: "createdAt",
              key: "createdAt",
            },
            {
              title: "操作",
              key: "action",
              width: 120,
              render: (_, record) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleSelectCustomerAsStaff(record)}
                >
                  选中为员工
                </Button>
              ),
            },
          ]}
          dataSource={customerDataSource}
          rowKey="id"
          loading={customerLoading}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* 修改资料弹窗 */}
      <Modal
        title={`修改资料 - ${currentRecord?.name || "未知员工"}`}
        open={profileModalVisible}
        onOk={handleProfileConfirm}
        onCancel={handleProfileCancel}
        width={800}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={profileForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: "请输入姓名" }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: "请选择性别" }]}
              >
                <Select placeholder="请选择性别">
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="age"
                label="年龄"
                rules={[{ required: true, message: "请输入年龄" }]}
              >
                <InputNumber
                  placeholder="请输入年龄"
                  min={18}
                  max={65}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="联系手机"
                rules={[
                  { required: true, message: "请输入手机号" },
                  { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="家庭住址"
            rules={[{ required: true, message: "请输入家庭住址" }]}
          >
            <Input.TextArea placeholder="请输入家庭住址" rows={2} />
          </Form.Item>

          <Form.Item
            name="idCard"
            label="身份证号码"
            rules={[
              { required: true, message: "请输入身份证号码" },
              {
                pattern:
                  /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dX]$/,
                message: "请输入正确的身份证号码",
              },
            ]}
          >
            <Input placeholder="请输入身份证号码" />
          </Form.Item>

          <Form.Item
            name="joinDate"
            label="入职日期"
            rules={[{ required: true, message: "请选择入职日期" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="title" label="职位标签">
            <Input placeholder="请输入职位标签（如：高级护理师、护理助理等）" />
          </Form.Item>

          <Form.Item label="头像">
            <ImageUpload
              value={imageUploadValues.avatar}
              onChange={(url) => updateImageUploadValue("avatar", url)}
              uploadPath="avatars/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="身份证正面照片">
                <ImageUpload
                  value={imageUploadValues.idCardFront}
                  onChange={(url) => updateImageUploadValue("idCardFront", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="身份证反面照片">
                <ImageUpload
                  value={imageUploadValues.idCardBack}
                  onChange={(url) => updateImageUploadValue("idCardBack", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="健康证照片">
            <ImageUpload
              value={imageUploadValues.healthCert}
              onChange={(url) => updateImageUploadValue("healthCert", url)}
              uploadPath="health-certs/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改统计弹窗 */}
      <Modal
        title={`修改统计信息 - ${currentRecord?.name || "未知员工"}`}
        open={statsModalVisible}
        onOk={handleStatsConfirm}
        onCancel={handleStatsCancel}
        width={600}
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={statsForm} layout="vertical">
          <Form.Item
            name="serviceCount"
            label="服务次数"
            rules={[
              { required: true, message: "请输入服务次数" },
              { type: "number", min: 0, message: "服务次数不能小于0" },
            ]}
          >
            <InputNumber
              placeholder="请输入服务次数（只能整数）"
              min={0}
              style={{ width: "100%" }}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            name="customerCount"
            label="当前客户数"
            rules={[
              { required: true, message: "请输入当前客户数" },
              { type: "number", min: 0, message: "客户数不能小于0" },
            ]}
          >
            <InputNumber
              placeholder="请输入当前客户数（只能整数）"
              min={0}
              style={{ width: "100%" }}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            name="rating"
            label="服务评价"
            rules={[
              { required: true, message: "请输入服务评价" },
              {
                pattern: /^([0-4](\.\d)?|5(\.0)?)$/,
                message: "请输入0-5之间的数字，最多一位小数",
              },
            ]}
          >
            <Input
              placeholder="请输入服务评价（0-5分，如：4.5）"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增员工弹窗 */}
      <Modal
        title={`新增员工 - ${selectedCustomer?.name || "未知客户"}`}
        open={addStaffModalVisible}
        onOk={handleAddStaffConfirm}
        onCancel={handleAddStaffCancel}
        width={800}
        okText="确认新增"
        cancelText="取消"
      >
        <Form
          form={approveForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: "请输入姓名" }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: "请选择性别" }]}
              >
                <Select placeholder="请选择性别">
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="age"
                label="年龄"
                rules={[{ required: true, message: "请输入年龄" }]}
              >
                <InputNumber
                  placeholder="请输入年龄"
                  min={18}
                  max={65}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="联系手机"
                rules={[
                  { required: true, message: "请输入手机号" },
                  { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="家庭住址"
            rules={[{ required: true, message: "请输入家庭住址" }]}
          >
            <Input.TextArea placeholder="请输入家庭住址" rows={2} />
          </Form.Item>

          <Form.Item
            name="idCard"
            label="身份证号码"
            rules={[
              { required: true, message: "请输入身份证号码" },
              {
                pattern:
                  /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dX]$/,
                message: "请输入正确的身份证号码",
              },
            ]}
          >
            <Input placeholder="请输入身份证号码" />
          </Form.Item>

          <Form.Item
            name="joinDate"
            label="入职日期"
            rules={[{ required: true, message: "请选择入职日期" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="title" label="职位标签">
            <Input placeholder="请输入职位标签（如：高级护理师、护理助理等）" />
          </Form.Item>

          <Form.Item label="头像">
            <ImageUpload
              value={imageUploadValues.avatar}
              onChange={(url) => updateImageUploadValue("avatar", url)}
              uploadPath="avatars/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="身份证正面照片"
                rules={[{ required: true, message: "请上传身份证正面照片" }]}
              >
                <ImageUpload
                  value={imageUploadValues.idCardFront}
                  onChange={(url) => updateImageUploadValue("idCardFront", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="身份证反面照片"
                rules={[{ required: true, message: "请上传身份证反面照片" }]}
              >
                <ImageUpload
                  value={imageUploadValues.idCardBack}
                  onChange={(url) => updateImageUploadValue("idCardBack", url)}
                  uploadPath="id-cards/"
                  accept="image/*"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="健康证照片"
            rules={[{ required: true, message: "请上传健康证照片" }]}
          >
            <ImageUpload
              value={imageUploadValues.healthCert}
              onChange={(url) => updateImageUploadValue("healthCert", url)}
              uploadPath="health-certs/"
              accept="image/*"
              maxCount={1}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 100px;
          }
        `,
        }}
      />
    </div>
  );
};

export default StaffManagement;
